/**
 * @file ComplaintImages.js
 * @version 1.0.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-22
 * @modified 2026-09-22
 * @description Photographs and video attached to a complaint.
 *
 *              A complaint about damaged or wrong-spec feed is mostly a
 *              photograph — a split bag, a wet pallet, a label that reads
 *              wrong. Everything else on the form describes the picture.
 *
 *              CREATE HAS NO ID YET. A complaint being created has no id until
 *              the save comes back, and there is nowhere to attach a file to
 *              until it does. So on create the files are held here as a pending
 *              list and uploaded by the parent the moment the save returns an
 *              id; on edit and view they are already on the server and this
 *              talks to it directly. `pendingFiles` is owned by the parent for
 *              exactly that reason — it is the parent that knows when the id
 *              exists.
 *
 *              The files live on disk under uploads/complaints/<id>/, which is
 *              a persistent mount deploy.sh excludes, and are copied to S3 by
 *              the nightly backup. The database holds only the index.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import './ComplaintImages.css';

const isMedia = (file) => file && /^(image|video)\//.test(file.type);
const isVideo = (item) => /^video\//.test(item?.mime_type || item?.type || '');

function ComplaintImages({ complaintId, readOnly = false, pendingFiles = [], onPendingChange }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(!!complaintId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The one being looked at full size.
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    if (!complaintId) { setImages([]); setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/complaints/${complaintId}/images`, { credentials: 'include' });
      const data = await res.json();
      setImages(data.success ? (data.images || []) : []);
    } catch (e) {
      console.error('Failed to load complaint images:', e);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [complaintId]);

  useEffect(() => { load(); }, [load]);

  const addFiles = (fileList) => {
    const picked = Array.from(fileList || []).filter(isMedia);
    if (picked.length === 0) return;
    setError(null);

    // No id yet: hand them to the parent to send once the save returns one.
    if (!complaintId) {
      onPendingChange?.([...pendingFiles, ...picked]);
      return;
    }

    (async () => {
      setBusy(true);
      try {
        const form = new FormData();
        picked.forEach(f => form.append('images', f));
        const res = await fetch(`${API_URL}/api/complaints/${complaintId}/images`, {
          method: 'POST', credentials: 'include', body: form
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Upload failed');
        await load();
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy(false);
      }
    })();
  };

  const removeSaved = async (imageId) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/complaints/${complaintId}/images/${imageId}`, {
        method: 'DELETE', credentials: 'include'
      });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const removePending = (index) =>
    onPendingChange?.(pendingFiles.filter((_, i) => i !== index));

  const src = (image) => `${API_URL}/api/complaints/${complaintId}/images/${image.id}`;
  const total = images.length + pendingFiles.length;

  return (
    <div className="complaint-images">
      <div className="complaint-images-head">
        <label>Photographs &amp; video{total > 0 ? ` (${total})` : ''}</label>
        {!readOnly && (
          <label className="btn-full complaint-image-add" title="Attach photographs">
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">add_a_photo</span>
            </div>
            <span className="btn-label">Add</span>
            {/* `capture` is deliberately absent: on a phone this offers the
                camera AND the library, which is what somebody standing in a
                yard actually wants. */}
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              disabled={busy}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
          </label>
        )}
      </div>

      {error && <div className="complaint-images-error">{error}</div>}

      {loading ? (
        <div className="complaint-images-empty">Loading…</div>
      ) : total === 0 ? (
        <div className="complaint-images-empty">
          {readOnly ? 'Nothing attached.' : 'No photographs or video yet.'}
        </div>
      ) : (
        <div className="complaint-image-grid">
          {images.map((image) => (
            <div key={`saved-${image.id}`} className="complaint-image">
              <button type="button" className="complaint-image-open"
                onClick={() => setViewing(image)} title={image.original_name}>
                {isVideo(image) ? (
                  <>
                    {/* #t=0.1 asks for a frame a tenth of a second in: many
                        clips open on a black frame, and a black square tells
                        nobody what the clip is of. preload="metadata" so a
                        wall of thumbnails does not fetch every video whole. */}
                    <video src={`${src(image)}#t=0.1`} preload="metadata" muted playsInline />
                    <span className="complaint-media-kind" title="Video">
                      <span className="material-symbols-rounded">play_circle</span>
                    </span>
                  </>
                ) : (
                  <img src={src(image)} alt={image.original_name} loading="lazy" />
                )}
              </button>
              {!readOnly && (
                <button type="button" className="btn-icon btn-danger complaint-image-remove"
                  onClick={() => removeSaved(image.id)} disabled={busy} title="Remove">
                  <span className="material-symbols-rounded">close</span>
                </button>
              )}
            </div>
          ))}

          {/* Not yet on the server. Shown from a blob so they look the same as
              the rest rather than as a list of filenames. */}
          {pendingFiles.map((file, i) => (
            <div key={`pending-${i}`} className="complaint-image is-pending">
              <span className="complaint-image-open">
                {isVideo(file) ? (
                  <video src={URL.createObjectURL(file)} preload="metadata" muted playsInline />
                ) : (
                  <img src={URL.createObjectURL(file)} alt={file.name} />
                )}
              </span>
              <span className="complaint-image-pending-tag" title="Attached when the complaint is saved">
                Pending
              </span>
              <button type="button" className="btn-icon btn-danger complaint-image-remove"
                onClick={() => removePending(i)} title="Remove">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div className="complaint-image-lightbox" onClick={() => setViewing(null)}>
          {isVideo(viewing) ? (
            <video src={src(viewing)} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={src(viewing)} alt={viewing.original_name} onClick={(e) => e.stopPropagation()} />
          )}
          <div className="complaint-image-lightbox-bar">
            <span>{viewing.original_name}</span>
            <a href={src(viewing)} download={viewing.original_name} onClick={(e) => e.stopPropagation()}>
              <span className="material-symbols-rounded">download</span>
            </a>
            <button type="button" onClick={() => setViewing(null)} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Send the files held back during a create, now that there is an id.
 * Called by the parent straight after the save returns. Resolves either way:
 * the complaint itself is already saved, and losing a photograph must not read
 * as the complaint having failed.
 */
export async function uploadPendingComplaintImages(complaintId, files) {
  if (!complaintId || !files || files.length === 0) return { success: true, uploaded: 0 };
  try {
    const form = new FormData();
    files.forEach(f => form.append('images', f));
    const res = await fetch(`${API_URL}/api/complaints/${complaintId}/images`, {
      method: 'POST', credentials: 'include', body: form
    });
    const data = await res.json();
    return { success: !!data.success, uploaded: data.images?.length || 0, error: data.error };
  } catch (e) {
    return { success: false, uploaded: 0, error: e.message };
  }
}

export default ComplaintImages;
